export type MasterLandingUrlFormat = "query" | "path" | "subdomain";

export interface MasterLandingUrlOptions {
  baseUrl?: string | null | undefined;
  format?: MasterLandingUrlFormat | string | null | undefined;
  useSlugSubdomains?: boolean | string | null | undefined;
}

const DEFAULT_LANDING_BASE_URL = "https://masters.zapis24.online";

export function buildMasterLandingUrl(
  slug: string,
  options: MasterLandingUrlOptions = {},
): string {
  const baseUrl = options.baseUrl ?? DEFAULT_LANDING_BASE_URL;
  const format = resolveLandingUrlFormat(options);

  try {
    const url = new URL(baseUrl);

    if (format === "subdomain" && url.hostname === "masters.zapis24.online") {
      return `https://${slug}.zapis24.online`;
    }

    if (format === "path") {
      url.pathname = appendPathSegment(url.pathname, slug);
      return url.toString();
    }

    url.searchParams.set("slug", slug);
    return url.toString();
  } catch {
    return buildFallbackLandingUrl(baseUrl, slug, format);
  }
}

function resolveLandingUrlFormat(options: MasterLandingUrlOptions): MasterLandingUrlFormat {
  const normalizedFormat = normalizeLandingUrlFormat(options.format);
  if (normalizedFormat) return normalizedFormat;
  return shouldUseSlugSubdomains(options.useSlugSubdomains) ? "subdomain" : "query";
}

function normalizeLandingUrlFormat(
  value: MasterLandingUrlFormat | string | null | undefined,
): MasterLandingUrlFormat | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "query" || normalized === "path" || normalized === "subdomain") {
    return normalized;
  }
  return null;
}

function shouldUseSlugSubdomains(value: boolean | string | null | undefined): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return false;
  return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
}

function appendPathSegment(pathname: string, slug: string): string {
  const basePath = pathname.replace(/\/+$/u, "");
  return `${basePath}/${encodeURIComponent(slug)}`;
}

function buildFallbackLandingUrl(
  baseUrl: string,
  slug: string,
  format: MasterLandingUrlFormat,
): string {
  const base = baseUrl.replace(/\/$/u, "");

  if (format === "path") {
    return `${base}/${encodeURIComponent(slug)}`;
  }

  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${new URLSearchParams({ slug }).toString()}`;
}
